const Form = require("components/forms/Form");
const Section = require("components/forms/Section");
const IBAN = require("components/forms/IBAN");
const CreditCard = require("components/forms/CreditCard");
const CheckboxGroup = require("components/forms/CheckboxGroup");
const Checkbox = require("components/forms/Checkbox");
const Confirm = require("components/modals/Confirm");
const Input = require("components/forms/Input");
const IntegerField = require("components/forms/IntegerField");
const Subusers = require("components/forms/Subusers");
const {decodeObservable} = require("helpers/object");
const store = require("stores/UserStore");
const env = require("services/env");

class TeacherSettingsForm extends Form {

	/////////////////////////////////
	// Accessors
	/////////////////////////////////

	get successMessage() {
		return "Dine indstillinger er blevet opdateret.";
	}

	get actionLabel() {
		return "Gem";
	}

	get subusersValue() {
		return decodeObservable(store.get('subusers', []));
	}

	get shouldWarn() {
		return true;
	}

	/////////////////////////////////
	// Methods
	/////////////////////////////////

	send(form) {
		if (!this.value('has_stripe_account')) {
			return this.creditCard.handle()
				.then(this.sendForm);
		}

		return this.iban.handle()
			.then(() => this.creditCard.handle())
			.then(this.sendForm);
	}

	sendForm = () => {
		return store.save(new FormData(this.form)).then((response) => {
			// Clear password inputs
			Array.from(this.form.querySelectorAll("input[type=password]")).forEach(input => input.value = "");
			return response;
		});
	}

	value(name) {
		return store.get(name);
	}

	get instantBookingSubtitle() {
		return "Øjeblikkelig booking tillader elever at booke dig til undervisning, i de tidsrum du har valgt under kalenderindstillinger, uden at du først skal godkende. Elever kan bruge dette, hvis de vil være sikker på et hurtigt svar, og din profil vil derudover blive prioriteret højere i søgninger.";
	}

	/////////////////////////////////
	// Renderers
	/////////////////////////////////

	renderIntantBookingSection() {
		return (
			<Section title="Øjeblikkelig booking"
							 subtitle={this.instantBookingSubtitle}
							 className="instant-booking">
				<div className="checkbox-wrap checkbox-wrap-special">
					<Checkbox name="instant_booking_enabled" label="Aktiver øjeblikkelig booking"
										checked={this.value("instant_booking_enabled")}/>
				</div>

				<IntegerField name="instant_booking_offset"
											label="Forhånds varsel (i timer)"
											tooltip="For at undgå overraskelsesbookinger i sidste øjeblik kan du indstille, hvor langt varsel du vil have, inden en booking."
											defaultValue={this.value("instant_booking_offset")}
											error={this.error("instant_booking_offset")}/>

				<IntegerField name="instant_booking_distance"
											label="Maks afstand (i km)"
											defaultValue={this.value("instant_booking_distance")}
											error={this.error("instant_booking_distance")}/>
			</Section>
		);
	}

	renderCardOptions() {
		return (
			<Section title="Betalingsoplysninger" contentClassName="flex">
				<CreditCard ref={el => this.creditCard = el} label="Kortoplysninger" name="card_token"
										afterChange={this.setWarning}
										currentCardType={this.value("card_brand")}
										currentCardDigits={this.value("card_last_four")}/>
			</Section>
		);
	}

	onStatusClick = (e) => {
		e.preventDefault();
		e.stopPropagation();

		const active = this.value('active', true);
		let awaitBody;
		let awaitTitle;

		if (active) {
			awaitTitle = 'Er du sikker på at du vil deaktivere din konto?';
			awaitBody = (
				<React.Fragment>
					<p>Din konto vil midlertidigt blive deaktiveret.</p>
					<p>Din profil vil ikke være synlig mens din konto er deaktiveret.</p>
					<p>Du har altid mulighed for at aktivere din konto igen.</p>
				</React.Fragment>
			);

		} else {
			awaitTitle = 'Er du sikker på at du vil aktivere din konto?';
			awaitBody = (
				<React.Fragment>
					<p>Din profil vil nu igen være synlig på siden.</p>
				</React.Fragment>
			);
		}

		return this.confirm
			.await(awaitBody, awaitTitle)
			.then(() => store.save({active: !active}))
			.then(() => window.location.reload())
			.catch();
	}

	renderAccountStatus() {
		const active = this.value('active', true);
		const word = active ?
			'Deaktiver' :
			'Aktiver';

		return (
			<Section title="Konto status">
				<a href='javascript:void()' onClick={this.onStatusClick} className="btn">{word} din konto</a>
			</Section>
		);
	}

	renderBody() {
		return (
			<div className="form-body">

				{this.renderBankOptions()}
				{this.renderCardOptions()}
				{this.renderIntantBookingSection()}
				{this.renderSubusers()}

				<Section title="Noftifikationer">
					<CheckboxGroup>{this.renderNotificationOptions()}</CheckboxGroup>
				</Section>

				{this.renderAccountStatus()}

				<Section title="Skift adgangskode" contentClassName="flex column">
					<Input type="password" label="Nuværende Adgangskode" name="password[current]" error={this.error("password.current")}/>
					<Input type="password" label="Ny Adgangskode" name="password[value]" error={this.error("password.value")}/>
					<Input type="password" label="Bekræft ny adgangskode" name="password[confirmation]"
								 error={this.error("password.confirmation")}/>
				</Section>
				<Confirm ref={el => this.confirm = el}/>
			</div>
		);
	}

	renderBankOptions() {
		if (!this.value('has_stripe_account')) return;

		return (
			<Section title="Udbetalingsoplysninger" contentClassName="flex">
				<IBAN ref={el => this.iban = el} label="IBAN nummer" name="bank_token"
							afterChange={this.setWarning}
							currentBankName={this.value("bank_name")}
							currentBankCountry={this.value("bank_country")}
							currentBankDigits={this.value("bank_last_four")} />
			</Section>
		);
	}

	renderNotificationOptions() {
		const options = env.get("options.notificationOptions", {});
		delete options.chat_reminder; // special case for teachers

		const userOptions = this.value("options", []);
		const isChecked = option => {
			const opt = userOptions.filter(userOpt => userOpt.name === `${option}_notifications`) [0];
			return (opt == undefined) || (opt.value === true);
		};

		return Object.keys(options).map(option => {
			return (
				<Checkbox key={option} name={`${option}_notifications`} label={options[option]} checked={isChecked(option)}/>
			);
		});
	}

	renderSubusers() {
		return (
			<Section title="Undervisningsmodtager (indstilling hvis du også er elev)">
				<Subusers name="subusers" error={this.errors("subusers")} value={this.subusersValue} openOnload={false}/>
			</Section>
		);
	}

}

module.exports = TeacherSettingsForm;



// WEBPACK FOOTER //
// ./resources/js/components/backend/TeacherSettingsForm.js